/* eslint-disable import/prefer-default-export */
import { useCallback, useState } from "react";
import { formSubmission } from "../../analytics/adobeDataLayer";
import { saveNotification } from "../../apicalls/notificationFunctions";
import type {
  AppNotification,
  NotificationCategory,
  NotificationFormState,
} from "../../models/Notification/notificationTypes";
import { toAppNotification } from "../../models/Notification/notificationTypes";
import type { Attachment } from "./useNotificationUpload";

type UseNotificationSaveOptions = {
  category: NotificationCategory;
  notification?: AppNotification;
  analyticsLabel: string;
  onClose: (saved?: AppNotification) => void;
  resetSaving: () => void;
};

type UseNotificationSaveResult = {
  saveError: string | null;
  persistToDb: (
    values: NotificationFormState,
    attachments: Attachment[],
  ) => Promise<void>;
};

export function useNotificationSave({
  category,
  notification,
  analyticsLabel,
  onClose,
  resetSaving,
}: UseNotificationSaveOptions): UseNotificationSaveResult {
  const [saveError, setSaveError] = useState<string | null>(null);

  const persistToDb = useCallback(
    async (values: NotificationFormState, attachments: Attachment[]) => {
      await formSubmission(
        notification ? `Edit ${analyticsLabel}` : `New ${analyticsLabel}`,
        notification ? "edit" : "new",
      );

      const uploadedKeys = attachments
        .filter((a) => a.source === "new")
        .map((a) => a.key);

        const removedAttachmentKeys = new Set(
        attachments
          .filter((a) => a.isRemoved && a.source === "existing")
          .map((a) => a.key),
      );

      const existing = notification?.attachmentKeys ?? [];
      const merged = [
        ...existing.filter((key) => !removedAttachmentKeys.has(key)),
        ...uploadedKeys,
      ];

      const finalAttachmentKeys = Array.from(new Set(merged));

      const formValues = {
        id: values.id || undefined,
        title: values.title,
        message: values.message,
        category,
        publishAt: values.publishAt,
        expiresAt: values.expiresAt || undefined,
        active: values.active,
        isWelcome: values.isWelcome,
        groups: values.groups,
        attachmentKeys: finalAttachmentKeys,
      };

      setSaveError(null);
      try {
        const raw = await saveNotification(formValues);
        onClose(toAppNotification(raw));
      } catch (err: unknown) {
        // eslint-disable-next-line no-console
        console.error("useNotificationSave.persistToDb:", err);
        setSaveError("NOTIFICATIONS.SAVE_ERROR");
        resetSaving();
      }
    },
    [category, notification, analyticsLabel, onClose, resetSaving],
  );

  return { saveError, persistToDb };
}
